import { EXECUTIVE_CONSTRAINT_FIELDS, missingExecutiveConstraints } from '../models/executiveConstraints'

export const EXECUTABLE_CANDIDATE_STATUS = Object.freeze({
    NOT_AVAILABLE: 'NOT_AVAILABLE',
    NEEDS_CONSTRAINTS: 'NEEDS_CONSTRAINTS',
    CANDIDATE_AVAILABLE: 'CANDIDATE_AVAILABLE',
    CONSTRAINT_CONFLICT: 'CONSTRAINT_CONFLICT'
})

const hasValue = (value) => value !== null && value !== undefined && value !== ''
const numberOrNull = (value) => (hasValue(value) && Number.isFinite(Number(value)) ? Number(value) : null)
const firstValue = (...values) => values.find(hasValue)
const firstNumber = (...values) => {
    const value = values.find((candidate) => numberOrNull(candidate) !== null)
    return value === undefined ? null : Number(value)
}

const evidenceReference = (type, reference, label) => (hasValue(reference) ? { type, reference, label } : null)

const normalizeAppliedConstraints = (constraints = {}) =>
    EXECUTIVE_CONSTRAINT_FIELDS.map((field) => ({ field, value: constraints?.[field] ?? null }))

const constrainedPosition = (portfolioData = {}) => {
    const constrained = portfolioData?.constrainedEconomicOptimization
    const optimal = constrained?.optimalPortfolio
    const capital = firstNumber(constrained?.selectedInvestment, optimal?.investment)
    if (!constrained || capital === null) return null
    return {
        id: firstValue(constrained.id, optimal?.id, 'constrained-analytical-position'),
        type: 'EXISTING_CONSTRAINED_ANALYTICAL_POSITION',
        label: 'Existing constrained portfolio position',
        capital,
        expectedResilienceImprovement: firstValue(optimal?.expectedRiskReduction, constrained?.capturedRiskReduction),
        residualExposure: firstValue(optimal?.residualExposure, constrained?.residualExposure),
        status: constrained.status,
        applicable: constrained.status == null || constrained.status === 'OPTIMAL' || constrained.status === 'AVAILABLE'
    }
}

const capitalEfficientPosition = (portfolioData = {}, recommendation = {}) => {
    const unconstrained = portfolioData?.unconstrainedEconomicOptimization
    const optimal = unconstrained?.optimalPortfolio
    const efficient = portfolioData?.capitalEfficientPosition ?? portfolioData?.capital_efficient_position ?? null
    const capital = firstNumber(efficient?.investment, efficient?.capitalRequired, optimal?.investment, recommendation?.financialImpact?.capitalRequired)
    if (capital === null) return null
    return {
        id: firstValue(efficient?.id, optimal?.id, recommendation?.id, 'capital-efficient-analytical-position'),
        type: 'EXISTING_CAPITAL_EFFICIENT_ANALYTICAL_POSITION',
        label: 'Existing capital-efficient analytical position',
        capital,
        expectedResilienceImprovement: firstValue(efficient?.expectedRiskReduction, optimal?.expectedRiskReduction, recommendation?.expectedImpact, recommendation?.rationale),
        residualExposure: firstValue(efficient?.residualExposure, optimal?.residualExposure),
        status: firstValue(efficient?.status, unconstrained?.status),
        applicable: firstValue(efficient?.status, unconstrained?.status) == null || ['OPTIMAL', 'AVAILABLE', 'SUPPORTED'].includes(firstValue(efficient?.status, unconstrained?.status))
    }
}

export function synthesizeExecutableCandidate({ decisionContext, executiveConstraints } = {}) {
    const portfolioEngine = decisionContext?.engines?.portfolio
    const portfolioData = portfolioEngine?.data ?? decisionContext?.portfolioData ?? null
    const recommendation = portfolioEngine?.executiveRecommendation ?? decisionContext?.recommendation ?? null
    const contextReference = firstValue(decisionContext?.reference, decisionContext?.id, portfolioData?.id, portfolioEngine?.execution?.candidateComparisonReference)
    const positions = [constrainedPosition(portfolioData), capitalEfficientPosition(portfolioData, recommendation)].filter(
        (position) => position?.applicable
    )
    const evidenceReferences = [
        evidenceReference('DECISION_CENTER_CONTEXT', contextReference, 'Decision Center context'),
        evidenceReference('PORTFOLIO_ANALYSIS', portfolioData?.id, 'Portfolio Analysis evidence'),
        evidenceReference('CANDIDATE_COMPARISON', portfolioEngine?.execution?.candidateComparisonReference, 'Candidate comparison'),
        evidenceReference('SOURCE_POSITION', positions[0]?.id, positions[0]?.label)
    ].filter(Boolean)

    if (!portfolioData || positions.length === 0) {
        return {
            status: EXECUTABLE_CANDIDATE_STATUS.NOT_AVAILABLE,
            feasibility: 'NOT_AVAILABLE',
            feasibilityRationale: 'Required analytical evidence is missing; no Executable Candidate can be synthesized.',
            constraintGaps: ['Required analytical evidence is missing.'],
            appliedConstraints: normalizeAppliedConstraints(executiveConstraints),
            evidenceReferences
        }
    }

    const missing = missingExecutiveConstraints(executiveConstraints)
    if (missing.length) {
        return {
            status: EXECUTABLE_CANDIDATE_STATUS.NEEDS_CONSTRAINTS,
            feasibility: 'NEEDS_CONSTRAINTS',
            feasibilityRationale: 'Candidate synthesis requires the remaining Executive Constraints.',
            constraintGaps: missing.map((field) => `${field} is required.`),
            appliedConstraints: normalizeAppliedConstraints(executiveConstraints),
            evidenceReferences
        }
    }

    const availableCapital = numberOrNull(executiveConstraints.availableCapital)
    const supported = positions.find((position) => availableCapital === null || position.capital <= availableCapital)
    if (!supported) {
        return {
            status: EXECUTABLE_CANDIDATE_STATUS.CONSTRAINT_CONFLICT,
            feasibility: 'CONSTRAINT_CONFLICT',
            feasibilityRationale: 'No analytically supported position satisfies the current capital constraint.',
            appliedConstraints: normalizeAppliedConstraints(executiveConstraints),
            constraintGaps: ['Available Capital is below every supported analytical position.'],
            evidenceReferences
        }
    }

    return {
        status: EXECUTABLE_CANDIDATE_STATUS.CANDIDATE_AVAILABLE,
        sourcePosition: { id: supported.id, type: supported.type, label: supported.label },
        courseOfAction: firstValue(recommendation?.title, decisionContext?.executiveDecision?.recommendation?.headline, supported.label),
        indicativeCapital: supported.capital,
        expectedResilienceImprovement: supported.expectedResilienceImprovement ?? null,
        residualExposure: supported.residualExposure ?? null,
        feasibility: 'FEASIBLE_FROM_EXISTING_EVIDENCE',
        feasibilityRationale: `The available evidence supports a candidate based on the ${supported.label.toLowerCase()}.`,
        appliedConstraints: normalizeAppliedConstraints(executiveConstraints),
        constraintGaps: [],
        evidenceReferences: [
            ...evidenceReferences.filter((reference) => reference.type !== 'SOURCE_POSITION'),
            evidenceReference('SOURCE_POSITION', supported.id, supported.label)
        ].filter(Boolean)
    }
}

export default synthesizeExecutableCandidate
